import { Injectable, Injector } from "@angular/core";
import {
	NavigationEnd,
	RouteConfigLoadEnd,
	RouteConfigLoadStart,
	Router,
	RouterPreloader,
	RoutesRecognized,
} from "@angular/router";
import { PageIdentifier, RouterLibCommand } from "@fabric-types/lib-router";
import { FetchStatusType } from "@obg/ngrx-utils";
import { DocumentRef } from "@obg/ngx.global.evo";
import { get } from "lodash-es";
import { BehaviorSubject, merge, Observable, ReplaySubject, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, mergeMap, take, tap } from "rxjs/operators";

import { RouteConfig } from "../config/router-config.model";

import { LazyLoadSuccessPayload } from "./lazy-loading.model";

/** @internal */
@Injectable({
	providedIn: "root",
})
export class LazyLoadingService {
	private preload$ = new ReplaySubject<string>();
	private currentFetchStatus: Record<string, FetchStatusType> = {};
	private fetchStatus$ = new BehaviorSubject(this.currentFetchStatus);
	private lazyLoadSuccess$ = new Subject<LazyLoadSuccessPayload>();
	private routerEvents$$: Subscription | undefined;
	private hasFirstNavigationEnded = false;

	constructor(
		private injector: Injector,
		private router: Router,
		private documentRef: DocumentRef
	) {}

	initialize() {
		if (this.routerEvents$$) {
			return;
		}
		const loadStart$ = this.router.events.pipe(
			filter((event) => event instanceof RouteConfigLoadStart),
			map((loadStartEvent: RouteConfigLoadStart) => loadStartEvent.route as RouteConfig),
			filter((route) => this.currentFetchStatus[route.name] !== FetchStatusType.Loading),
			tap((route) => {
				this.currentFetchStatus = {
					...this.currentFetchStatus,
					[route.name]: FetchStatusType.Loading,
				};
				this.fetchStatus$.next(this.currentFetchStatus);
			})
		);

		const loadEnd$ = this.router.events.pipe(
			filter((event) => event instanceof RouteConfigLoadEnd),
			mergeMap((loadEndEvent: RouteConfigLoadEnd) =>
				merge(
					// wait for routes recognized so that `_loadedConfig.routes` is set
					this.router.events.pipe(
						filter((event) => event instanceof RoutesRecognized),
						take(1)
					),
					// add an async fallback in case navigation does not trigger (navigate by route)
					Promise.resolve()
				).pipe(
					take(1),
					map(() => loadEndEvent.route as RouteConfig),
					filter((route) => this.currentFetchStatus[route.name] !== FetchStatusType.Success),
					tap((route) => {
						this.addPagesAndTranslationToLibRouter(
							this.router.config as RouteConfig[],
							route._loadedRoutes
						);

						this.lazyLoadSuccess$.next({
							parentRoute: route,
							loadedChildren: get(route, "_loadedConfig._loadedRoutes") ?? [],
						});

						this.currentFetchStatus = {
							...this.currentFetchStatus,
							[route.name]: FetchStatusType.Success,
						};
						this.fetchStatus$.next(this.currentFetchStatus);
					})
				)
			)
		);

		const firstNavigationEnd$ = this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			take(1),
			tap(() => (this.hasFirstNavigationEnded = true))
		);

		this.routerEvents$$ = merge(loadStart$, loadEnd$, firstNavigationEnd$).subscribe();
	}

	dispose() {
		if (!this.routerEvents$$) {
			return;
		}

		this.routerEvents$$.unsubscribe();
		this.routerEvents$$ = undefined;
	}

	loadModule(config: RouteConfig): Observable<FetchStatusType | undefined> {
		if (!this.getModuleStatus(config.name)) {
			if (!this.hasFirstNavigationEnded) {
				(this.injector.get(RouterPreloader) as any).preloadConfig(this.injector, config).subscribe();
			}
			this.preload$.next(config.name);
		}
		return this.getModuleStatus$(config.name);
	}

	getModuleStatus(name: string): FetchStatusType | undefined {
		return this.currentFetchStatus[name];
	}

	getModuleStatus$(name: string): Observable<FetchStatusType | undefined> {
		return this.fetchStatus$.asObservable().pipe(
			map((status) => status[name]),
			distinctUntilChanged()
		);
	}

	getLazyLoadSuccess$(): Observable<LazyLoadSuccessPayload> {
		return this.lazyLoadSuccess$.asObservable();
	}

	getPreload$(): Observable<string> {
		return this.preload$.asObservable();
	}

	private addPagesAndTranslationToLibRouter(routes: RouteConfig[], newRoutes: RouteConfig[] = []): void {
		if (!this.documentRef.obgEventBus) {
			return;
		}
		const pageIdentifiers: PageIdentifier[] = [];
		const translationMap: { urlTranslated: string; urlLogical: string }[] = [];

		newRoutes
			.filter((route) => route.path !== "")
			.forEach((route) => {
				const pageId = route.name;
				const pathParts = pageId.split(".");
				let routeString = "/";
				let translatedRouteString = "/";

				pathParts.pop();

				pathParts.forEach((_, index) => {
					const pathPageId = pathParts.slice(0, index + 1).join(".");

					const partRoute = routes.find((r) => r.name === pathPageId);

					routeString += partRoute?.originalPath + "/";
					translatedRouteString += partRoute?.path + "/";
				});

				routeString += route?.originalPath?.replace(":", "@");
				translatedRouteString += route?.path?.replace(":", "@");

				pageIdentifiers.push({
					pageId: route.name,
					routeString,
				});

				translationMap.push({
					urlLogical: routeString,
					urlTranslated: translatedRouteString,
				});
			});

		this.documentRef.obgEventBus?.dispatch(RouterLibCommand.AddRoutes, {
			pageIdentifiers,
			translationMap,
		});
	}
}
